import { makeAutoObservable } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats } from 'shared/lib'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { CalendarFilterEnum } from 'shared/ui/CalendarDropdown/calendarEnum'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IAnalyticsBaseParams } from 'entities/Analytics/api/types'
import { CreditUsageAnalyticsApi } from 'entities/Analytics/api/creditUsageAnalytics'
import { ExportAnalyticsModalStore } from 'widgets/ExportAnalyticsModal'
import { SourcesTableStore } from 'pages/analytics/pages/creditUsage/pages/sources/store/sourcesTableStore'
import { SourceGroupFilterStore } from 'pages/analytics/pages/creditUsage/pages/sources/store/sourceGroupFilterStore'
import { SourcesSummaryStore } from 'pages/analytics/pages/creditUsage/pages/sources/store/sourcesSummaryStore'

export class CreditUsageSourcesStore {
  private readonly _calendarFilterStore: CalendarFilterStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _sourceGroupFilterStore: SourceGroupFilterStore
  private readonly _sourcesTableStore: SourcesTableStore
  private readonly _sourcesSummaryStore: SourcesSummaryStore

  constructor(entities: IEntitiesStore) {
    this._calendarFilterStore = new CalendarFilterStore(entities.usersStore.user?.createdAt || null)
    this._calendarFilterStore.setSelectedCalendarFilter(CalendarFilterEnum.last30days)
    this._calendarFilterStore.setParamsDate()
    this._sourceGroupFilterStore = new SourceGroupFilterStore()
    this._sourcesTableStore = new SourcesTableStore(
      () => this.filtersParams,
      () => this._sourceGroupFilterStore.selectedSourceGroupKeys
    )
    this._sourcesSummaryStore = new SourcesSummaryStore(
      () => this.filtersParams,
      () => this._sourceGroupFilterStore.selectedSourceGroupKeys
    )
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore((params) =>
      CreditUsageAnalyticsApi.exportSources({
        ...this._sourcesTableStore.exportParams,
        format: params.format,
      })
    )

    makeAutoObservable(this)
  }

  get filtersParams(): IAnalyticsBaseParams {
    const { sendDate } = this._calendarFilterStore

    return {
      from: sendDate?.from
        ? uiStore.dayjsWithoutTimezone(sendDate.from).format(DayjsFormats.timestampServerDate)
        : '',
      to: sendDate?.to
        ? uiStore.dayjsWithoutTimezone(sendDate.to).format(DayjsFormats.timestampServerDate)
        : '',
      timezone: uiStore.timezone,
      interval: 'week',
    }
  }

  init = () => {
    this._sourcesTableStore.init()
    this._sourcesSummaryStore.init()
  }

  openExportModal = () => {
    this._exportAnalyticsModalStore.onOpenExport(this.filtersParams)
  }

  get calendarFilterStore() {
    return this._calendarFilterStore
  }

  get sourceGroupFilterStore() {
    return this._sourceGroupFilterStore
  }

  get sourcesTableStore() {
    return this._sourcesTableStore
  }

  get sourcesSummaryStore() {
    return this._sourcesSummaryStore
  }

  dispose = () => {
    this._exportAnalyticsModalStore.dispose()
    this._sourcesTableStore.dispose()
    this._sourcesSummaryStore.dispose()
  }
}
